'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib';
import { X } from 'lucide-react';
import * as React from 'react';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'bg-opacity-90 bg-grey-50/70 dark:bg-grey-1100/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogSize = 'sm' | 'md' | 'lg';

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    size?: DialogSize;
  }
>(({ className, children, hideCloseButton = false, size = 'sm', ...props }, ref) => {
  const getWidth = () => {
    switch (size) {
      case 'lg':
        return 'w-[90dvw] sm:w-[600px] xl:w-[1352px]';
      case 'md':
        return 'w-[90dvw] sm:w-[600px]';
      case 'sm':
        return 'w-[90dvw] sm:w-[478px]';
      default:
        return 'w-[90dvw] sm:w-[478px]';
    }
  };

  // Check for DialogDescription in children on every render
  const hasDialogDescription = React.useMemo(() => {
    return React.Children.toArray(children).some(
      (child) => React.isValidElement(child) && child.type === DialogPrimitive.Description
    );
  }, [children]);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'dark:bg-grey-1000 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4 fixed top-[50%] left-[50%] z-[999] inline-flex max-h-[80vh] w-[335px] translate-x-[-50%] translate-y-[-50%] flex-col items-start justify-start gap-3 overflow-hidden rounded-[32px] bg-neutral-100 shadow-[0px_17px_38px_0px_rgba(42,43,65,0.05),0px_69px_69px_0px_rgba(42,43,65,0.04),0px_154px_93px_0px_rgba(42,43,65,0.03),0px_275px_110px_0px_rgba(42,43,65,0.01),0px_429px_120px_0px_rgba(42,43,65,0.00)] duration-200 lg:max-h-[85vh]',
          getWidth(),
          className
        )}
        {...props}
      >
        <div className="dialog-scrollbar h-full w-full overflow-y-scroll p-8 sm:p-11">
          <div className="relative flex w-full flex-col items-end justify-start gap-5">
            {!hideCloseButton && (
              <DialogPrimitive.Close asChild>
                <Button
                  variant="primary-icon"
                  className="[&[data-variant*=icon]]:h-8 [&[data-variant*=icon]]:w-8 [&[data-variant*=icon]_svg]:h-[24px] [&[data-variant*=icon]_svg]:w-[24px]"
                >
                  <X />
                </Button>
              </DialogPrimitive.Close>
            )}
            {children}
            {/* Automatically add DialogDescription if none provided */}
            {!hasDialogDescription && (
              <DialogPrimitive.Description className="sr-only">
                Dialog content
              </DialogPrimitive.Description>
            )}
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex w-full flex-col space-y-2 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex w-full flex-col gap-4', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-[26px] md:text-[32px] leading-[32px] md:leading-[40px] font-bold text-neutral-200 dark:text-neutral-100',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-grey-600 dark:text-grey-400 text-sm leading-tight font-normal', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
